// Shared Business Metrics / Time Analysis charts.
// Pages render a filter row and empty chart cards; this module fetches the page's JSON and
// draws stat tiles, Highcharts charts and table views. Every visual option is set per chart,
// because older pages change Highcharts' global options.

const COLORS = {
  surface: '#fcfcfb',
  grid: '#e6e5e1',
  text: '#0b0b0b',
  muted: '#52514e',
  billable: '#2a78d6',   // categorical slot 1 (blue)
  nonBillable: '#eb6834' // categorical slot 2 (orange)
};
const FONT = 'inherit';

const escapeHtml = (value) => String(value == null ? '' : value)
  .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

const formatNumber = (value) => {
  const n = Number(value) || 0;
  return Math.abs(n) >= 10000 ? (n / 1000).toFixed(1) + 'K' : n.toLocaleString(undefined, { maximumFractionDigits: 1 });
};

const formatMinutes = (minutes) => {
  const h = Math.floor(minutes / 60);
  const m = Math.round(minutes % 60);
  return h ? (m ? h + 'h ' + m + 'm' : h + 'h') : m + 'm';
};

// ---------- chart chrome ----------

const baseOptions = (container, extra) => Highcharts.merge({
  chart: {
    renderTo: container,
    backgroundColor: COLORS.surface,
    plotBackgroundColor: null,
    borderWidth: 0,
    plotBorderWidth: 0,
    plotShadow: false,
    style: { fontFamily: FONT },
    spacing: [12, 24, 12, 12]
  },
  title: { text: null },
  credits: { enabled: false },
  exporting: { enabled: false },
  legend: {
    itemStyle: { color: COLORS.muted, fontWeight: 'normal', fontSize: '13px' },
    symbolRadius: 2
  },
  xAxis: {
    labels: { enabled: true, style: { color: COLORS.muted, fontSize: '12px' } },
    lineColor: COLORS.grid,
    tickColor: COLORS.grid,
    gridLineWidth: 0
  },
  yAxis: {
    title: { text: null },
    labels: { enabled: true, style: { color: COLORS.muted, fontSize: '12px' } },
    gridLineColor: COLORS.grid,
    gridLineWidth: 1,
    gridLineDashStyle: 'Solid'
  },
  tooltip: {
    backgroundColor: '#ffffff',
    borderColor: COLORS.grid,
    borderRadius: 6,
    shadow: false,
    useHTML: true,
    style: { color: COLORS.text, fontSize: '13px' }
  },
  plotOptions: {
    series: { animation: { duration: 250 }, borderWidth: 0, states: { inactive: { opacity: 0.4 } } },
    bar: { pointWidth: 18, groupPadding: 0.1, pointPadding: 0.05, borderWidth: 0 },
    column: { maxPointWidth: 24, groupPadding: 0.1, pointPadding: 0.05, borderWidth: 0 }
  }
}, extra || {});

const barHeight = (rows) => Math.max(160, rows * 30 + 70);

// One ranked series: names on the axis, value at each bar tip, no legend
const rankedBars = (container, rows, { color, suffix, max }) => new Highcharts.Chart(baseOptions(container, {
  chart: { type: 'bar', height: barHeight(rows.length) },
  legend: { enabled: false },
  // Long names wrap instead of being cut off
  xAxis: { categories: rows.map((r) => r.name), labels: { style: { textOverflow: 'none', whiteSpace: 'normal', width: 180 } } },
  yAxis: { min: 0, max: max || null, maxPadding: 0.12, labels: { format: '{value}' + suffix, style: { textOverflow: 'none' } } },
  tooltip: {
    formatter: function () {
      return '<b>' + formatNumber(this.y) + suffix + '</b><br><span style="color:' + COLORS.muted + '">' + escapeHtml(this.x) + '</span>';
    }
  },
  plotOptions: {
    bar: {
      dataLabels: {
        enabled: true, crop: false, overflow: 'allow',
        style: { color: COLORS.text, fontWeight: '600', fontSize: '12px', textOutline: 'none' },
        formatter: function () { return formatNumber(this.y) + suffix; }
      }
    }
  },
  series: [{ name: 'value', data: rows.map((r) => r.value), color: color }]
}));

// Billable / non-billable stacked into one bar per category
const billableBars = (container, rows, { type, categories, xAxis }) => new Highcharts.Chart(baseOptions(container, {
  chart: { type: type, height: type === 'bar' ? barHeight(rows.length) : 320 },
  xAxis: Highcharts.merge({ categories: categories, labels: { style: { textOverflow: 'none', whiteSpace: 'normal', width: 180 } } }, xAxis || {}),
  yAxis: { min: 0, reversedStacks: false, maxPadding: 0.05, labels: { format: '{value}h', style: { textOverflow: 'none' } } },
  tooltip: {
    shared: true,
    formatter: function () {
      const total = this.points.reduce((sum, p) => sum + p.y, 0);
      let html = '<b>' + formatNumber(total) + 'h total</b><br><span style="color:' + COLORS.muted + '">' + escapeHtml(this.x) + '</span>';
      this.points.forEach((p) => {
        html += '<br><span style="display:inline-block;width:10px;height:2px;margin-right:6px;vertical-align:middle;background:' + p.color + '"></span>' +
          '<b>' + formatNumber(p.y) + 'h</b> ' + escapeHtml(p.series.name);
      });
      return html;
    }
  },
  plotOptions: { series: { stacking: 'normal', borderWidth: 1, borderColor: COLORS.surface } },
  series: [
    { name: 'Billable', data: rows.map((r) => r.billable), color: COLORS.billable },
    { name: 'Non-billable', data: rows.map((r) => r.non_billable), color: COLORS.nonBillable }
  ]
}));

// One series of columns over time (e.g. training hours per week or day)
const singleColumns = (container, categories, values, { suffix, color }) => new Highcharts.Chart(baseOptions(container, {
  chart: { type: 'column', height: 300 },
  legend: { enabled: false },
  xAxis: { categories: categories, labels: { style: { textOverflow: 'none' } } },
  yAxis: { min: 0, labels: { format: '{value}' + suffix } },
  tooltip: {
    formatter: function () {
      return '<b>' + formatNumber(this.y) + suffix + '</b><br><span style="color:' + COLORS.muted + '">' + escapeHtml(this.x) + '</span>';
    }
  },
  series: [{ name: 'value', data: values, color: color || COLORS.billable }]
}));

// ---------- page pieces ----------

const renderTiles = (el, tiles) => {
  el.textContent = '';
  tiles.forEach(([label, value, accent]) => {
    const tile = document.createElement('div');
    tile.className = 'metric-tile' + (accent ? ' metric-tile-' + accent : '');
    const v = document.createElement('div');
    v.className = 'metric-tile-value';
    v.textContent = value;
    const l = document.createElement('div');
    l.className = 'metric-tile-label';
    l.textContent = label;
    tile.append(v, l);
    el.append(tile);
  });
};

// Table twin of a chart: header row + rows, all inserted as text
const renderTable = (card, headers, rows) => {
  const holder = card.querySelector('.metric-table');
  holder.textContent = '';
  const table = document.createElement('table');
  table.className = 'table table-sm';
  const head = table.createTHead().insertRow();
  headers.forEach((h) => { const th = document.createElement('th'); th.textContent = h; head.append(th); });
  const body = table.createTBody();
  rows.forEach((cells) => {
    const tr = body.insertRow();
    cells.forEach((c) => { tr.insertCell().textContent = c; });
  });
  holder.append(table);
};

const renderEmpty = (card, isEmpty) => card.classList.toggle('is-empty', isEmpty);

// Cards marked data-hide-when-single disappear when there is nothing to compare (0 or 1 rows)
const hideWhenSingle = (card, rows) => { card.hidden = card.dataset.hideWhenSingle === '1' && rows.length < 2; };

// Ranked bar card with its table twin
const rankedCard = (charts, card, name, rows, headers, options) => {
  if (charts[name]) { charts[name].destroy(); }
  charts[name] = rankedBars(card.querySelector('.metric-chart'), rows, options);
  renderTable(card, headers, rows.map((r) => [r.name, r.value]));
  renderEmpty(card, rows.length === 0);
};

// Billable (blue) / non-billable (orange) bars per category, with a three-column table twin
const billableCard = (charts, card, name, rows, label) => {
  if (charts[name]) { charts[name].destroy(); }
  charts[name] = billableBars(card.querySelector('.metric-chart'), rows, { type: 'bar', categories: rows.map((r) => r.name) });
  renderTable(card, [label, 'Billable hours', 'Non-billable hours'], rows.map((r) => [r.name, r.billable, r.non_billable]));
  renderEmpty(card, rows.length === 0);
};

const weekLabel = (iso) => 'Week of ' + new Date(iso + 'T00:00:00').toLocaleDateString(undefined, { month: 'short', day: 'numeric' });

const wireTableToggles = (root) => {
  root.querySelectorAll('.metric-card').forEach((card) => {
    const button = card.querySelector('.metric-table-toggle');
    if (!button || button.dataset.wired) { return; }
    button.dataset.wired = '1';
    button.addEventListener('click', () => {
      const showing = card.classList.toggle('show-table');
      button.textContent = showing ? 'Show chart' : 'Show table';
      button.setAttribute('aria-pressed', showing);
    });
  });
};

// ---------- filters ----------

const isoDate = (d) => d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0');

const presetRange = (preset, schoolYear) => {
  const today = new Date();
  switch (preset) {
    case 'this_month': return [new Date(today.getFullYear(), today.getMonth(), 1), new Date(today.getFullYear(), today.getMonth() + 1, 0)];
    case 'last_month': return [new Date(today.getFullYear(), today.getMonth() - 1, 1), new Date(today.getFullYear(), today.getMonth(), 0)];
    case 'last_30': return [new Date(today.getFullYear(), today.getMonth(), today.getDate() - 29), today];
    case 'school_year': return [schoolYear.start, schoolYear.end];
    default: return null;
  }
};

const setupFilters = (root, onChange) => {
  const form = root.querySelector('.metric-filters');
  const schoolYear = { start: new Date(form.dataset.schoolYearStart + 'T00:00:00'), end: new Date(form.dataset.schoolYearEnd + 'T00:00:00') };
  const preset = form.querySelector('[name=preset]');
  const start = form.querySelector('[name=start_date]');
  const end = form.querySelector('[name=end_date]');

  preset.addEventListener('change', () => {
    const range = presetRange(preset.value, schoolYear);
    if (range) { start.value = isoDate(range[0]); end.value = isoDate(range[1]); }
    onChange();
  });
  [start, end].forEach((input) => input.addEventListener('change', () => { preset.value = 'custom'; onChange(); }));
  form.querySelectorAll('select:not([name=preset])').forEach((select) => select.addEventListener('change', onChange));
  form.addEventListener('submit', (e) => { e.preventDefault(); onChange(); });

  return () => {
    const params = new URLSearchParams(new FormData(form));
    params.delete('preset');
    [...params.keys()].forEach((key) => { if (!params.get(key)) { params.delete(key); } });
    return params;
  };
};

// Fetch JSON for the current filters, keep the URL in step, dim (don't blank) while loading
const loader = (root, url, render) => {
  let request = 0;
  return (params) => {
    const id = ++request;
    window.history.replaceState(window.history.state, '', window.location.pathname + '?' + params.toString());
    root.classList.add('is-loading');
    return fetch(url + '?' + params.toString(), { headers: { Accept: 'application/json' }, credentials: 'same-origin' })
      .then((response) => { if (!response.ok) { throw new Error(response.status); } return response.json(); })
      .then((data) => { if (id === request) { render(data); } })
      .catch(() => { root.querySelector('.metric-error').hidden = false; })
      .finally(() => { if (id === request) { root.classList.remove('is-loading'); } });
  };
};

// ---------- Business Metrics page ----------

const businessMetrics = (root) => {
  const charts = {};
  const card = (name) => root.querySelector('[data-card=' + name + ']');
  const replace = (name, build) => { if (charts[name]) { charts[name].destroy(); } charts[name] = build(); };
  const breakoutSelect = root.querySelector('[name=breakout]');

  const render = (data) => {
    root.querySelector('.metric-error').hidden = true;
    const s = data.summary;
    renderTiles(root.querySelector('.metric-tiles'), [
      ['Total hours', formatNumber(s.total_hours)],
      ['Billable hours', formatNumber(s.billable_hours), 'billable'],
      ['Non-billable hours', formatNumber(s.total_hours - s.billable_hours), 'non-billable'],
      ['Billable', s.billable_percent + '%'],
      ['Utilization', s.utilization + '%'],
      ['Sessions', formatNumber(s.sessions)],
      ['Staff', formatNumber(s.staff)]
    ]);

    replace('utilization', () => rankedBars(card('utilization').querySelector('.metric-chart'), data.utilization, { color: COLORS.billable, suffix: '%' }));
    renderTable(card('utilization'), ['Staff member', 'Utilization %'], data.utilization.map((r) => [r.name, r.value]));
    renderEmpty(card('utilization'), data.utilization.length === 0);

    replace('hours', () => billableBars(card('hours').querySelector('.metric-chart'), data.hours_by_person, { type: 'bar', categories: data.hours_by_person.map((r) => r.name) }));
    renderTable(card('hours'), ['Staff member', 'Billable hours', 'Non-billable hours'], data.hours_by_person.map((r) => [r.name, r.billable, r.non_billable]));
    renderEmpty(card('hours'), data.hours_by_person.length === 0);

    const weeks = data.hours_by_week.map((r) => weekLabel(r.week));
    replace('weekly', () => billableBars(card('weekly').querySelector('.metric-chart'), data.hours_by_week, { type: 'column', categories: weeks }));
    renderTable(card('weekly'), ['Week', 'Billable hours', 'Non-billable hours'], data.hours_by_week.map((r, i) => [weeks[i], r.billable, r.non_billable]));
    renderEmpty(card('weekly'), data.hours_by_week.length === 0);

    billableCard(charts, card('service_types'), 'service_types', data.hours_by_service_type, 'Service type');
    hideWhenSingle(card('service_types'), data.hours_by_service_type);

    // Breakout choices come from the data; keep the selection when it still applies
    const current = String(data.breakout.service_type_id || '');
    breakoutSelect.textContent = '';
    data.breakout.options.forEach((o) => breakoutSelect.add(new Option(o.name, o.id)));
    breakoutSelect.value = current;
    const breakoutName = breakoutSelect.selectedOptions[0] ? breakoutSelect.selectedOptions[0].text : 'the selected service type';
    card('breakout').querySelector('.metric-card-title').textContent = 'Hours by contract: ' + breakoutName;
    billableCard(charts, card('breakout'), 'breakout', data.breakout.rows, 'Contract');

    billableCard(charts, card('instruction_types'), 'instruction_types', data.hours_by_instruction_type, 'Instruction type');
    hideWhenSingle(card('instruction_types'), data.hours_by_instruction_type);
  };

  wireTableToggles(root);
  const load = loader(root, root.dataset.url, render);
  const params = setupFilters(root, () => load(params()));
  load(params());
};

// ---------- Time Analysis page ----------

const clockMs = (clock) => { const [h, m] = clock.split(':').map(Number); return Date.UTC(2000, 0, 1, h, m); };

const timeAnalysis = (root) => {
  let chart = null;
  const card = root.querySelector('[data-card=timeline]');

  const render = (data) => {
    root.querySelector('.metric-error').hidden = true;
    const t = data.timeline;
    const days = t.days;
    const avgGap = days.length ? days.reduce((sum, d) => sum + d.gap, 0) / days.length : 0;
    renderTiles(root.querySelector('.metric-tiles'), [
      ['Days worked', formatNumber(days.length)],
      ['Hours', formatNumber(data.summary.total_hours)],
      ['Non-billable hours', formatNumber(data.summary.total_hours - data.summary.billable_hours), 'non-billable'],
      ['Billable', data.summary.billable_percent + '%'],
      ['Utilization', data.summary.utilization + '%'],
      ['Average gap per day', formatMinutes(avgGap)]
    ]);

    const dateLabels = days.map((d) => new Date(d.date + 'T00:00:00').toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' }));
    const rowOf = Object.fromEntries(days.map((d, i) => [d.date, i]));
    const point = (s) => ({ x: clockMs(s.start), x2: clockMs(s.end), y: rowOf[s.date], session: s });
    if (chart) { chart.destroy(); }
    chart = new Highcharts.Chart(baseOptions(card.querySelector('.metric-chart'), {
      chart: { type: 'xrange', height: Math.max(200, days.length * 30 + 90) },
      legend: { enabled: true },
      xAxis: {
        type: 'datetime', min: clockMs('06:00'), max: clockMs('18:00'), tickInterval: 3600 * 1000,
        gridLineWidth: 1, gridLineColor: COLORS.grid, opposite: true,
        labels: { format: '{value:%l %p}' }
      },
      yAxis: { categories: dateLabels, reversed: true, gridLineWidth: 0, labels: { style: { color: COLORS.muted } } },
      tooltip: {
        formatter: function () {
          const s = this.point.session;
          return '<b>' + formatMinutes(s.minutes) + '</b> &middot; ' + escapeHtml(s.start) + '–' + escapeHtml(s.end) +
            '<br>' + escapeHtml(s.contract) + '<br><span style="color:' + COLORS.muted + '">' + escapeHtml(s.service_type) +
            (s.students.length ? '<br>' + escapeHtml(s.students.join(', ')) : '') + '</span>';
        }
      },
      // Color by series (not per point) so the legend shows billable / non-billable colors
      plotOptions: { xrange: { colorByPoint: false, pointWidth: 18, borderRadius: 2, borderWidth: 1, borderColor: COLORS.surface, dataLabels: { enabled: false } } },
      series: [
        { name: 'Billable', color: COLORS.billable, data: t.sessions.filter((s) => s.billable).map(point) },
        { name: 'Non-billable', color: COLORS.nonBillable, data: t.sessions.filter((s) => !s.billable).map(point) }
      ]
    }));
    renderTable(card, ['Date', 'Start', 'End', 'Minutes', 'Contract', 'Service type', 'Students', 'Billable'],
      t.sessions.map((s) => [s.date, s.start, s.end, s.minutes, s.contract, s.service_type, s.students.join(', '), s.billable ? 'Yes' : 'No']));
    renderEmpty(card, t.sessions.length === 0);
  };

  wireTableToggles(root);
  const load = loader(root, root.dataset.url, render);
  const params = setupFilters(root, () => load(params()));
  load(params());
};

// ---------- Training & Consultation pages ----------

const trainingMetrics = (root) => {
  const charts = {};
  const card = (name) => root.querySelector('[data-card=' + name + ']');

  const render = (data) => {
    root.querySelector('.metric-error').hidden = true;
    const s = data.summary;
    renderTiles(root.querySelector('.metric-tiles'), [
      ['Trainings', formatNumber(s.trainings)],
      ['Training hours', formatNumber(s.hours)],
      ['Attendees', formatNumber(s.attendees)],
      ['Trainers', formatNumber(s.trainers)],
      ['Districts', formatNumber(s.districts)]
    ]);

    const weeks = data.hours_by_week.map((r) => weekLabel(r.week));
    if (charts.weekly) { charts.weekly.destroy(); }
    charts.weekly = singleColumns(card('weekly').querySelector('.metric-chart'), weeks, data.hours_by_week.map((r) => r.hours), { suffix: 'h' });
    renderTable(card('weekly'), ['Week', 'Hours'], data.hours_by_week.map((r, i) => [weeks[i], r.hours]));
    renderEmpty(card('weekly'), data.hours_by_week.length === 0);

    [['by_initiative', ['Initiative', 'Trainings'], ''], ['hours_by_initiative', ['Initiative', 'Hours'], 'h'],
      ['attendees_by_initiative', ['Initiative', 'Attendees'], ''], ['by_trainer', ['Trainer', 'Trainings'], ''],
      ['by_activity', ['Activity type', 'Trainings'], ''], ['attendees_by_activity', ['Activity type', 'Attendees'], ''],
      ['by_district', ['District', 'Trainings'], ''], ['by_audience', ['Audience', 'Trainings'], '']
    ].forEach(([name, headers, suffix]) => rankedCard(charts, card(name), name, data[name], headers, { color: COLORS.billable, suffix: suffix }));
  };

  wireTableToggles(root);
  const load = loader(root, root.dataset.url, render);
  const params = setupFilters(root, () => load(params()));
  load(params());
};

const trainingTimeAnalysis = (root) => {
  let chart = null;
  const card = root.querySelector('[data-card=daily]');

  const render = (data) => {
    root.querySelector('.metric-error').hidden = true;
    const days = data.hours_by_day;
    const total = days.reduce((sum, d) => sum + d.hours, 0);
    renderTiles(root.querySelector('.metric-tiles'), [
      ['Training days', formatNumber(days.length)],
      ['Training hours', formatNumber(data.summary.hours)],
      ['Trainings', formatNumber(data.summary.trainings)],
      ['Average hours per day', formatNumber(days.length ? total / days.length : 0)]
    ]);
    const labels = days.map((d) => new Date(d.date + 'T00:00:00').toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' }));
    if (chart) { chart.destroy(); }
    chart = singleColumns(card.querySelector('.metric-chart'), labels, days.map((d) => d.hours), { suffix: 'h' });
    renderTable(card, ['Date', 'Hours'], days.map((d, i) => [labels[i], d.hours]));
    renderEmpty(card, days.length === 0);
  };

  wireTableToggles(root);
  const load = loader(root, root.dataset.url, render);
  const params = setupFilters(root, () => load(params()));
  load(params());
};

const MetricsCharts = {
  init: () => {
    document.querySelectorAll('[data-metrics-page]').forEach((root) => {
      if (root.dataset.started) { return; }
      root.dataset.started = '1';
      if (root.dataset.metricsPage === 'business') { businessMetrics(root); }
      if (root.dataset.metricsPage === 'time') { timeAnalysis(root); }
      if (root.dataset.metricsPage === 'training') { trainingMetrics(root); }
      if (root.dataset.metricsPage === 'training_time') { trainingTimeAnalysis(root); }
    });
  }
};

export { MetricsCharts };
